import type { AppShell } from '@shared/ui/shell';
import { toActivity } from '@shared/ui/track';
import { useAuth } from '../contexts/AuthContext';
import { useAnalysisType, type AnalysisTypeId } from '../contexts/AnalysisTypeContext';
import { useSubscriptionAccess } from '../hooks/useSubscriptionAccess';
import { useAffiliateTracking } from '../hooks/useAffiliateTracking';
import { usePromoTracking } from '../hooks/usePromoTracking';
import analyticsService from '../services/analytics';
import ProtectedRoute from '../components/ProtectedRoute';
import SubscriptionRoute from '../components/SubscriptionRoute';
import TipsPaywall from '../components/tips/TipsPaywall';

/**
 * Connects the shared customer UI to this app's auth, subscription and
 * analytics (ADR-0008). Temporary: goes when the app shells are merged.
 */
// Shared events keep this brand's historical names so existing reports continue.
const EVENT_NAMES: Record<string, string> = { high_probability_view: 'tippa_picks', high_probability_view_v2: 'tippa_picks_v2' };

export const shell: AppShell = {
  useViewer() {
    const { isAuthenticated, isLoading, user } = useAuth();
    const { currentAnalysisType, availableAnalysisTypes, canSwitchAnalysisType, setAnalysisType, hasTrackStats, hasMultis, hasMultiBuilder, hasChat } = useAnalysisType();
    const { isSubscribed, isLoading: subLoading } = useSubscriptionAccess();
    return {
      isAuthenticated,
      loading: isLoading || (isAuthenticated && subLoading),
      authLoading: isLoading,
      firstName: user?.firstName,
      analysisTypeId: currentAnalysisType || 1,
      // staff-only: the shared race header shows a model switch when there is a choice
      analysisTypes: canSwitchAnalysisType ? availableAnalysisTypes.map((t) => ({ id: t.id, name: t.name, title: t.title })) : undefined,
      setAnalysisType: canSwitchAnalysisType ? (id: number) => setAnalysisType(id as AnalysisTypeId) : undefined,
      isSubscribed: isAuthenticated && isSubscribed,
      trackStats: hasTrackStats,
      multis: hasMultis,
      multiBuilder: hasMultiBuilder,
      chat: hasChat,
    };
  },
  track(event, params) {
    analyticsService.trackEvent(EVENT_NAMES[event] ?? event, toActivity(params));
  },
  Protected: ProtectedRoute,
  Subscribed: SubscriptionRoute,
  Paywall: TipsPaywall,
  useCheckoutCodes() {
    const { getAffiliateForRegistration } = useAffiliateTracking();
    const { promotion, getUserDiscountCode } = usePromoTracking();
    const { isAuthenticated } = useAuth();
    return {
      affiliateCode: () => getAffiliateForRegistration() ?? null,
      userDiscountCode: async () => (isAuthenticated ? (await getUserDiscountCode()) ?? null : null),
      promotionCode: promotion?.discountCode ?? null,
    };
  },
};
